import TwilioVoiceClient from 'dashboard/api/channel/voice/twilioVoiceClient';
import FreeswitchVoiceClient from 'dashboard/api/channel/voice/freeswitchVoiceClient';
import { cleanupWhatsappSession } from 'dashboard/composables/useWhatsappCallSession';
import { VOICE_CALL_PROVIDERS } from 'dashboard/helper/inbox';
import { TERMINAL_STATUSES } from 'dashboard/helper/voice';
import { defineStore } from 'pinia';

const teardownByProvider = call => {
  if (call?.provider === VOICE_CALL_PROVIDERS.WHATSAPP || call?.provider === 'whatsapp') {
    cleanupWhatsappSession();
  } else if (
    call?.provider === VOICE_CALL_PROVIDERS.FREESWITCH ||
    call?.provider === 'freeswitch' ||
    call?.provider === 'webrtc'
  ) {
    FreeswitchVoiceClient.endClientCall(false);
  } else {
    TwilioVoiceClient.endClientCall();
  }
};

export const useCallsStore = defineStore('calls', {
  state: () => ({
    calls: [],
  }),

  getters: {
    activeCall: state => state.calls.find(call => call.isActive) || null,
    hasActiveCall: state => state.calls.some(call => call.isActive),
    incomingCalls: state => state.calls.filter(call => !call.isActive),
    hasIncomingCall: state => state.calls.some(call => !call.isActive),
  },

  actions: {
    handleCallStatusChanged({ callSid, status, conversationId }) {
      if (!TERMINAL_STATUSES.includes(status)) return;

      const call = this.calls.find(
        c =>
          c.callSid === callSid ||
          c.callId === callSid ||
          (conversationId && c.conversationId === conversationId)
      );

      if (call) {
        this.calls = this.calls.filter(c => c !== call);
        teardownByProvider(call);
      } else {
        this.clearActiveCall();
      }
    },

    addCall(callData) {
      if (!callData?.callSid) return;

      // 1. Exact ID match
      let existing = this.calls.find(
        c =>
          c.callSid === callData.callSid ||
          c.callId === callData.callSid ||
          (callData.callId &&
            (c.callId === callData.callId || c.callSid === callData.callId))
      );

      // 2. Outbound call deduplication: an agent can only make ONE outbound call at a time.
      if (!existing && callData.callDirection === 'outbound') {
        existing = this.calls.find(c => c.callDirection === 'outbound');
      }

      // 3. Inbound call deduplication by phone or conversation
      if (!existing && callData.callDirection === 'inbound') {
        const newPhone = (
          callData.caller?.phone ||
          callData.caller?.phoneNumber ||
          ''
        ).replace(/\D/g, '');

        existing = this.calls.find(c => {
          if (c.callDirection === 'inbound') {
            const oldPhone = (
              c.caller?.phone ||
              c.caller?.phoneNumber ||
              ''
            ).replace(/\D/g, '');
            if (
              newPhone &&
              oldPhone &&
              (newPhone === oldPhone ||
                newPhone.endsWith(oldPhone) ||
                oldPhone.endsWith(newPhone))
            ) {
              return true;
            }
            if (
              c.conversationId &&
              callData.conversationId &&
              c.conversationId === callData.conversationId
            ) {
              return true;
            }
          }
          return false;
        });
      }

      if (existing) {
        const next = { ...callData };
        if (existing.caller && !next.caller) delete next.caller;
        if (callData.callSid) existing.callSid = callData.callSid;
        if (callData.callId) existing.callId = callData.callId;
        if (callData.conversationId)
          existing.conversationId = callData.conversationId;
        if (callData.inboxId) existing.inboxId = callData.inboxId;
        if (callData.provider) existing.provider = callData.provider;
        Object.assign(existing, next, { isActive: existing.isActive });
        return;
      }

      // Prepend so the newest call surfaces as the primary card (incomingCalls[0])
      this.calls.unshift({
        ...callData,
        isActive: false,
      });
    },

    removeCall(callSid) {
      const callToRemove = this.calls.find(
        c => c.callSid === callSid || c.callId === callSid
      );
      if (!callToRemove) return;
      this.calls = this.calls.filter(
        c => c.callSid !== callSid && c.callId !== callSid
      );
      if (callToRemove.isActive) {
        teardownByProvider(callToRemove);
      }
    },

    setCallActive(callSid) {
      if (!callSid || this.calls.length === 1) {
        this.calls = this.calls.map(call => ({ ...call, isActive: true }));
        return;
      }
      const matchFound = this.calls.some(
        call => call.callSid === callSid || call.callId === callSid
      );
      if (matchFound) {
        this.calls = this.calls.map(call => ({
          ...call,
          isActive: call.callSid === callSid || call.callId === callSid,
        }));
      } else {
        // If the active callSid from WebRTC doesn't match by exact string,
        // activate the existing outbound call
        const outbound = this.calls.find(
          call => call.callDirection === 'outbound'
        );
        if (outbound) {
          this.calls = this.calls.map(call => ({
            ...call,
            isActive: call === outbound,
          }));
        }
      }
    },

    clearActiveCall() {
      const active = this.calls.find(c => c.isActive);
      if (active) {
        const sid = active.callSid;
        this.calls = this.calls.filter(call => !call.isActive && call.callSid !== sid && call.callId !== sid);
        teardownByProvider(active);
      }
      if (!this.calls.some(c => c.isActive)) {
        const remaining = [...this.calls];
        this.calls = [];
        remaining.forEach(c => teardownByProvider(c));
      }
    },

    dismissCall(callSid) {
      this.calls = this.calls.filter(call => call.callSid !== callSid);
    },

    removeCallsForConversation(conversationId) {
      const callsToRemove = this.calls.filter(
        call => call.conversationId === conversationId
      );

      // Tear down each active call via its own provider so a WhatsApp call
      // gets cleanupWhatsappSession() (closes pc, stops recorder/mic) instead
      // of the Twilio-only endClientCall() — otherwise mic stays open.
      callsToRemove.filter(call => call.isActive).forEach(teardownByProvider);

      this.calls = this.calls.filter(
        call => call.conversationId !== conversationId
      );
    },
  },
});
